// src/hooks/useVisitorCounter.js
import { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, increment, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase/firebaseconfig';

const useVisitorCounter = () => {
  const [isNewVisitor, setIsNewVisitor] = useState(false);
  const [visitorStats, setVisitorStats] = useState({
    totalVisitors: 0,
    monthlyVisitors: 0
  });
  const hasTracked = useRef(false); // Prevent double execution

  useEffect(() => {
    const trackVisitor = async () => {
      // Prevent double execution in React Strict Mode
      if (hasTracked.current) {
        return;
      }

      try {
        // Check if user has visited before using localStorage
        const hasVisited = localStorage.getItem('burotime_hasVisited');
        const visitDate = localStorage.getItem('burotime_lastVisit');
        const sessionTracked = sessionStorage.getItem('burotime_sessionTracked');
        const today = new Date().toDateString();

        // Only track if it's a new visitor/day AND not already tracked in this session
        if ((!hasVisited || visitDate !== today) && !sessionTracked) {
          hasTracked.current = true; // Set ref flag

          // New visitor or returning after a day
          setIsNewVisitor(true);

          // Update Firebase counter
          const statsRef = doc(db, 'siteStats', 'visitors');
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth() + 1;
          const monthlyKey = `visits_${currentYear}_${currentMonth}`;

          // Check if document exists
          const statsDoc = await getDoc(statsRef);

          if (statsDoc.exists()) {
            // Update existing counter
            await updateDoc(statsRef, {
              totalVisitors: increment(1),
              lastUpdated: new Date(),
              [monthlyKey]: increment(1)
            });

            // Update local state with new values
            const updatedStats = statsDoc.data();
            setVisitorStats({
              totalVisitors: (updatedStats.totalVisitors || 0) + 1,
              monthlyVisitors: (updatedStats[monthlyKey] || 0) + 1
            });
          } else {
            // Create new counter document
            await setDoc(statsRef, {
              totalVisitors: 1,
              lastUpdated: new Date(),
              [monthlyKey]: 1
            });

            setVisitorStats({
              totalVisitors: 1,
              monthlyVisitors: 1
            });
          }

          // Mark as visited in localStorage and session
          localStorage.setItem('burotime_hasVisited', 'true');
          localStorage.setItem('burotime_lastVisit', today);
          sessionStorage.setItem('burotime_sessionTracked', 'true');

          console.log('✅ New visitor tracked successfully');
        } else {
          hasTracked.current = true; // Still set the ref to prevent re-runs
          setIsNewVisitor(false);
          console.log('👋 Returning visitor - not tracking');

          // Still fetch current stats for display
          const statsRef = doc(db, 'siteStats', 'visitors');
          const statsDoc = await getDoc(statsRef);
          
          if (statsDoc.exists()) {
            const stats = statsDoc.data();
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            const monthlyKey = `visits_${currentYear}_${currentMonth}`;
            
            setVisitorStats({
              totalVisitors: stats.totalVisitors || 0,
              monthlyVisitors: stats[monthlyKey] || 0
            });
          }
        }
      } catch (error) {
        console.error('Error tracking visitor:', error);
        // Reset the flag on error so it can try again
        hasTracked.current = false;
      }
    };

    trackVisitor();
  }, []); // Empty dependency array

  return { isNewVisitor, visitorStats };
};

export default useVisitorCounter;